// components/Contact/ContactForm.tsx
import React from 'react';
import { useForm } from 'react-hook-form';
import { TextField, Button, Box, Alert } from '@mui/material';
import { emailService } from '../../services/emailService';
import { trackContactSubmission } from '../../utils/analytics';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ContactFormData>();
  const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle');

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Use the email service to send contact message
      const contactData = {
        name: data.name,
        email: data.email,
        subject: data.subject,
        message: data.message,
      };

      // Track contact form submission
      trackContactSubmission(contactData.subject || 'General Inquiry');
      
      const result = await emailService.sendContactEmail(contactData);

      if (result.success) {
        setSubmitStatus('success');
        reset();
      } else {
        setSubmitStatus('error');
        // eslint-disable-next-line no-console
        console.error('Email service error:', result.message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending contact message:', error);
      setSubmitStatus('error');
    }
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit(onSubmit)} 
      sx={{ 
        mt: { xs: 2, md: 3 },
        px: { xs: 1, md: 0 }
      }}
    >
      {submitStatus === 'success' && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '0.875rem', md: '1rem' },
            borderRadius: '12px'
          }}
        >
          Your message has been sent successfully! We'll get back to you soon.
        </Alert>
      )}
      {submitStatus === 'error' && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '0.875rem', md: '1rem' },
            borderRadius: '12px'
          }}
        >
          There was an error sending your message. Please try again or contact us directly.
        </Alert>
      )}
      
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: { xs: 2, md: 2.5 }
      }}>
        <TextField
          fullWidth
          label="Name *"
          {...register('name', { required: 'Name is required' })}
          error={!!errors.name}
          helperText={errors.name?.message}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '12px',
              fontSize: { xs: '1rem', md: '1rem' }
            },
            '& .MuiInputBase-input': {
              padding: { xs: '16px 14px', md: '16px 14px' }
            }
          }}
        />
        
        <TextField
          fullWidth
          label="Email *"
          type="email"
          {...register('email', { 
            required: 'Email is required',
            pattern: {
              value: /^\S+@\S+$/i,
              message: 'Invalid email address'
            }
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '12px',
              fontSize: { xs: '1rem', md: '1rem' }
            },
            '& .MuiInputBase-input': {
              padding: { xs: '16px 14px', md: '16px 14px' }
            }
          }}
        />
        
        <TextField
          fullWidth
          label="Phone"
          {...register('phone', {
            pattern: {
              value: /^(\+91|91)?[6-9]\d{9}$/,
              message: 'Please enter a valid Indian mobile number (10 digits starting with 6-9)'
            }
          })}
          error={!!errors.phone}
          helperText={errors.phone?.message || 'Enter your mobile number for contact'}
          placeholder="+91 XXXXXXXXXX"
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '12px',
              fontSize: { xs: '1rem', md: '1rem' }
            },
            '& .MuiInputBase-input': {
              padding: { xs: '16px 14px', md: '16px 14px' }
            }
          }}
        />
        
        <TextField
          fullWidth
          label="Subject *"
          {...register('subject', { required: 'Subject is required' })}
          error={!!errors.subject}
          helperText={errors.subject?.message || 'What is your inquiry about?'}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '12px',
              fontSize: { xs: '1rem', md: '1rem' }
            },
            '& .MuiInputBase-input': {
              padding: { xs: '16px 14px', md: '16px 14px' }
            }
          }}
        />
        
        <TextField
          fullWidth
          label="Message *"
          multiline
          rows={4}
          {...register('message', { required: 'Message is required' })}
          error={!!errors.message}
          helperText={errors.message?.message || 'Please describe your inquiry in detail'}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '12px',
              fontSize: { xs: '1rem', md: '1rem' }
            },
            '& .MuiInputBase-input': {
              padding: { xs: '16px 14px', md: '16px 14px' }
            }
          }}
        />
        
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          sx={{ 
            mt: { xs: 1, md: 2 },
            py: { xs: 1.5, md: 1.75 },
            backgroundColor: '#ffca28',
            color: '#1a237e',
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 600,
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(255, 202, 40, 0.3)',
            '&:hover': {
              backgroundColor: '#ffd54f',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(255, 202, 40, 0.4)',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
              boxShadow: 'none'
            },
            transition: 'all 0.3s ease'
          }}
          disabled={submitStatus === 'success'}
        >
          {submitStatus === 'success' ? '✓ Message Sent!' : 'Send Message'}
        </Button>
      </Box>
    </Box>
  );
};

export default ContactForm;